<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import GiftVoucherTemplatePanel from './GiftVoucherTemplatePanel';
import TwWysiwyg from '../TwWysiwyg';

export default {
  name: 'GiftVoucherTemplateEdit',
  props: {
    template: {
      type: Object,
      default: undefined,
    },
    show: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
  },
  components: {
    GiftVoucherTemplatePanel,
    TwWysiwyg,
  },
  data() {
    return {
      preview: false,
      templateModel: {},
      dialog: false,
      showPanel: false,
      focus: undefined,
      focusName: undefined,
      error: false,
    };
  },
  computed: {
    ...mapState({
      save: state => state.giftVoucherTemplates.save,
    }),
    ...mapGetters({
      options: 'giftVoucherTemplates/getInsertOptions',
    }),
    validatedInput() {
      return !this.templateModel.name ||
        !this.templateModel.emailSubject ||
        !this.templateModel.emailContent ||
        !this.templateModel.pdfName ||
        !this.templateModel.pdfContent ||
        !this.templateModel.remark;
    },
  },
  methods: {
    ...mapActions({
      updateGiftVoucherTemplate: 'giftVoucherTemplates/updateGiftVoucherTemplate',
      addGiftVoucherTemplate: 'giftVoucherTemplates/addGiftVoucherTemplate',
    }),
    replaceValues(value) {
      let newValue = value;
      this.options.forEach((option) => {
        const regex = `:${option.value}:`;
        newValue = newValue.replace(new RegExp(regex, 'g'), option.example);
      });
      return newValue;
    },
    setFocus(event, name) {
      if (!event) {
        this.focus = undefined;
        this.focusName = undefined;
      } else {
        this.focus = event.target;
        this.focusName = name;
      }
    },
    setFroalaFocus(event, name) {
      this.focus = event;
      this.focusName = name;
    },
    insertValue(value) {
      if (this.focus) {
        if (this.focus.event) {
          this.focus.event.target.focus();
          this.focus.editor.html.insert(`:${value.value}:`);
        } else {
          const insert = this.focus.selectionStart;
          this.focus.focus();
          const text = this.templateModel[this.focusName];
          const textBefore = text.substring(0, insert);
          const textAfter = text.substring(insert, text.length);
          this.$set(this.templateModel, this.focusName, `${textBefore}:${value.value}:${textAfter}`);
        }
      }
    },
    previewTemplate() {
      this.preview = !this.preview;
    },
    saveTemplate() {
      if (this.save.loading) {
        return;
      }

      if (this.type === 'edit') {
        this.updateGiftVoucherTemplate(this.templateModel).then(() => {
          this.closeDialog();
        }).catch(() => {
          this.error = true;
        });
      } else {
        this.addGiftVoucherTemplate(this.templateModel).then(() => {
          this.closeDialog();
        }).catch(() => {
          this.error = true;
        });
      }
    },
    closeDialog() {
      this.dialog = false;
      this.showPanel = false;
    },
  },
  watch: {
    show() {
      this.showPanel = false;
      this.dialog = true;
      this.templateModel = Object.assign({}, this.template);
      setTimeout(() => {
        this.showPanel = true;
      }, 350);
    },
  },
};
</script>

<template>
  <v-container>
    <v-dialog
      v-model="dialog"
      max-width="60%"
      persistent
      no-click-animation
    >
      <v-card v-if="dialog">
        <v-card-title>
          <h1>{{ $t('giftVoucherTemplates.edit.title') }}</h1>
        </v-card-title>
        <v-divider></v-divider>
        <v-progress-linear
          class="my-0"
          v-if="save.loading"
          :active="save.loading"
          :indeterminate="true"
          :height="4"
        />
        <v-flex pa-4>
          <v-flex>
            <v-text-field
              v-model="templateModel.name"
              v-if="!preview"
              @focus="setFocus($event, 'name')"
              :label="$t('giftVoucherTemplates.edit.labels.name')"
              :disabled="save.loading"
            ></v-text-field>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.name') }}: </h3>
              {{ replaceValues(templateModel.name) }}
            </span>
          </v-flex>

          <v-flex>
            <v-text-field
              v-model="templateModel.monthsValidFor"
              v-if="!preview"
              @focus="setFocus"
              :label="$t('giftVoucherTemplates.edit.labels.monthsValidFor')"
              :disabled="save.loading"
              type="number"
            ></v-text-field>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.monthsValidFor') }}: </h3>
              {{ templateModel.monthsValidFor }}
            </span>
          </v-flex>

          <v-flex mt-5>
            <v-text-field
              v-model="templateModel.emailSubject"
              v-if="!preview"
              @focus="setFocus($event, 'emailSubject')"
              :label="$t('giftVoucherTemplates.edit.labels.emailSubject')"
              :disabled="save.loading"
              class="mt-3"
            ></v-text-field>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.emailSubject') }}: </h3>
              {{ replaceValues(templateModel.emailSubject) }}
            </span>
          </v-flex>

          <v-flex mt-2>
            <tw-wysiwyg
              v-if="!preview"
              @focus="setFroalaFocus($event, 'emailContent')"
              @change="templateModel.emailContent = $event"
              :value="templateModel.emailContent"
              :disabled="save.loading"
            >
            </tw-wysiwyg>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.emailContent') }}: </h3>
              <v-divider class="mt-2"></v-divider>
              <span v-html="replaceValues(templateModel.emailContent)"></span>
              <v-divider class="mt-2"></v-divider>
            </span>
          </v-flex>

          <v-flex mt-5>
            <v-text-field
              v-model="templateModel.pdfName"
              v-if="!preview"
              @focus="setFocus($event, 'pdfName')"
              :label="$t('giftVoucherTemplates.edit.labels.pdfName')"
              :disabled="save.loading"
            ></v-text-field>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.pdfName') }}: </h3>
              {{ replaceValues(templateModel.pdfName) }}
            </span>
          </v-flex>

          <v-flex mt-2>
            <tw-wysiwyg
              v-if="!preview"
              @focus="setFroalaFocus($event, 'pdfContent')"
              @change="templateModel.pdfContent = $event"
              :value="templateModel.pdfContent"
              :disabled="save.loading"
            >
            </tw-wysiwyg>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.pdfContent') }}: </h3>
              <v-divider class="mt-2"></v-divider>
              <span v-html="replaceValues(templateModel.pdfContent)"></span>
              <v-divider class="mt-2"></v-divider>
            </span>
          </v-flex>

          <v-flex mt-5 mb-5>
            <v-text-field
              v-model="templateModel.remark"
              v-if="!preview"
              @focus="setFocus($event, 'remark')"
              :label="$t('giftVoucherTemplates.edit.labels.remark')"
              :disabled="save.loading"
            ></v-text-field>
            <span v-else>
              <h3>{{ $t('giftVoucherTemplates.edit.labels.remark') }}: </h3>
              {{ replaceValues(templateModel.remark) }}
            </span>
          </v-flex>
        </v-flex>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="error">
      {{ $t('giftVoucherTemplates.edit.error') }}
    </v-snackbar>

    <gift-voucher-template-panel
      v-if="showPanel"
      @preview="previewTemplate"
      @value="insertValue"
      @save="saveTemplate"
      @close="closeDialog"
      :options="options"
      :preview="preview"
      :validated="validatedInput"
    />
  </v-container>
</template>



// WEBPACK FOOTER //
// src/components/gift-voucher-templates/GiftVoucherTemplateEdit.vue